// v7.50 — Página de Limpeza do Cardápio Fantasma (admin-only)
//
// CONTEXTO: Em produção, clientes do delivery viam pratos R$ 0,00 (ex:
// Strogonoff Premium, Combo Família Churrasco) que NÃO existiam no admin.
// Esses itens foram plantados por seeds locais antigos (KAVEN_DELIVERY_MENU)
// rodando em devices novos antes do Firebase responder o peek. Uma vez
// gravados, eram empurrados para o Firebase RTDB e contaminavam todos os
// devices.
//
// SOLUÇÃO PERMANENTE:
//   1. Os seeds foram REMOVIDOS de db.js (v7.50 — skipAllMenuSeeds=true).
//   2. Esta página permite ao admin LIMPAR fisicamente os itens fantasma
//      que ainda estão no Firebase RTDB de produção, criando tombstones
//      permanentes para que nenhum device os ressuscite.
//
// Acesso: somente admin via /admin/limpeza-fantasma

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle, Bug, RefreshCw, ArrowLeft, ShieldAlert, ChevronRight, Image as ImageIcon, ImageOff, CheckCircle2, XCircle, Upload, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { SyncAPI } from '@/lib/firebaseSync';
import db from '@/db';

/**
 * Nomes EXATOS dos pratos plantados pelos seeds antigos.
 * Esses são automaticamente marcados como fantasma. Caso o restaurante
 * realmente tenha um prato com esse nome cadastrado MANUALMENTE depois,
 * o admin pode desmarcar antes de deletar.
 */
const KNOWN_PHANTOM_NAMES = new Set([
  // Seed v0 (db.js linhas 700-711)
  'Quentinha de Carne',
  'Quentinha de Frango',
  'Quentinha de Peixe',
  'Marmita GG Carne',
  'Marmita GG Frango',
  'Refrigerante 2L',
  'Suco Natural 1L',
  // (Agua Mineral 500ml está em ambos seed v0 e v3.2 — incluímos)
  'Agua Mineral 500ml',
  // Seed v3 (KAVEN_DELIVERY_MENU)
  'Executivo de Frango Grelhado',
  'Executivo Parmegiana',
  'Executivo Costela Barbecue',
  'Executivo Cupim Assado',
  'Executivo Mineiro',
  'Box Fraldinha',
  'Box Fradininha', // typo já visto no vídeo do cliente
  'Box Costela BBQ',
  'Box Misto Kaven',
  'Penne 4 Queijos com Frango',
  'Lasanha Bolonhesa',
  'Escondidinho de Carne',
  'Fricassê de Frango',
  'Strogonoff Premium',
  'Combo Família Churrasco',
  'Combo Família Mineiro',
  'Combo Família Parmegiana',
  // Seed v3.2 bebidas (faltava preço)
  'Coca-Cola Lata 350ml',
  'Guaraná Antarctica 350ml',
  'Coca-Cola 2L',
  'Suco Natural 500ml',
  'Água Mineral 500ml',
]);

/**
 * Classifica um item como fantasma se:
 *   - Nome bate na lista conhecida E (preço == 0 OU sem imagem), OU
 *   - Preço é 0 E sem imagem (heurística genérica para itens R$ 0,00 não-cadastrados)
 */
function isLikelyPhantom(item) {
  const nameMatch = KNOWN_PHANTOM_NAMES.has(item.name);
  const priceZero = !item.price || Number(item.price) === 0;
  const noImage = !item.imageUrl || String(item.imageUrl).length < 5;
  if (nameMatch && (priceZero || noImage)) return true;
  if (priceZero && noImage) return true;
  return false;
}

export default function GhostMenuCleanerPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [remoteItems, setRemoteItems] = useState([]);
  const [localItems, setLocalItems] = useState([]);
  const [deviceGroup, setDeviceGroup] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showOnlyPhantom, setShowOnlyPhantom] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [wipeText, setWipeText] = useState('');
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // v7.53 — Sincronização e migração
  const [syncing, setSyncing] = useState(false);
  const [confirmNuclear, setConfirmNuclear] = useState(false);
  const [nukeText, setNukeText] = useState('');
  const [migrating, setMigrating] = useState(false);

  const CANONICAL_GROUP = 'kaven-default';
  const isDivergent = deviceGroup && deviceGroup !== CANONICAL_GROUP;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLastResult(null);
    try {
      const remote = await SyncAPI.readRemoteTable('menuItems');
      if (!remote.ok) {
        setError(`Não foi possível ler do Firebase: ${remote.reason}${remote.error ? ' — ' + remote.error : ''}`);
        setRemoteItems([]);
      } else {
        setRemoteItems(remote.items || []);
        setDeviceGroup(remote.deviceGroup);
      }
      const local = await db.menuItems.toArray();
      setLocalItems(local);
      setSelected(new Set());
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const phantoms = remoteItems.filter(isLikelyPhantom);
  const filteredItems = showOnlyPhantom ? phantoms : remoteItems;

  const toggle = (fbKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fbKey)) next.delete(fbKey);
      else next.add(fbKey);
      return next;
    });
  };

  const selectAllPhantom = () => {
    setSelected(new Set(phantoms.map((it) => it.fbKey)));
  };
  const clearSelection = () => setSelected(new Set());

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    let ok = 0;
    let fail = 0;
    const errors = [];
    for (const item of remoteItems) {
      if (!selected.has(item.fbKey)) continue;
      const res = await SyncAPI.deleteRemoteMenuItem({
        fbKey: item.fbKey,
        stableId: item.stableId || null,
        name: item.name || null,
      });
      if (res.ok) ok++;
      else { fail++; errors.push(`${item.name || item.fbKey}: ${res.error || res.reason}`); }
    }
    setDeleting(false);
    setConfirmBulk(false);
    setLastResult({ ok, fail, errors });
    if (ok > 0) toast.success(`${ok} item(ns) fantasma excluído(s) permanentemente.`);
    if (fail > 0) toast.error(`${fail} falha(s) ao excluir — veja detalhes abaixo.`);
    await refresh();
  };

  const handleWipeAll = async () => {
    setDeleting(true);
    const res = await SyncAPI.wipeRemoteMenuItems();
    setDeleting(false);
    setConfirmWipe(false);
    setWipeText('');
    if (res.ok) {
      toast.success(`${res.deletedCount || 0} pratos apagados do Firebase. ${res.tombstoneCount || 0} tombstones gravados.`);
      setLastResult({ ok: res.deletedCount, fail: 0, errors: [], wipe: true });
    } else {
      toast.error(`Falha ao limpar: ${res.reason} ${res.error || ''}`);
      setLastResult({ ok: 0, fail: 1, errors: [res.error || res.reason], wipe: true });
    }
    await refresh();
  };

  /**
   * v7.53 — Sincronização forçada: força push do Dexie local pro Firebase
   * + refresh do Firebase. Resolve o caso de itens cadastrados localmente
   * que não chegaram remotos (ex: rede caiu na hora do save).
   */
  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await SyncAPI.forcePushMenuItems();
      if (res.ok) {
        toast.success(`✓ ${res.pushed} de ${res.total} item(ns) enviados ao Firebase (${res.deviceGroup})`);
      } else {
        toast.error(`Falha ao sincronizar: ${res.reason} ${res.error || ''}`);
      }
    } finally {
      setSyncing(false);
      await refresh();
    }
  };

  /**
   * v7.53 — Nuclear wipe: apaga menuItems em TODOS os deviceGroups
   * conhecidos (atual + kaven-default), limpa Dexie local e tombstones.
   * Permite recomeçar do zero.
   */
  const handleNuclearWipe = async () => {
    setDeleting(true);
    try {
      // Inclui o atual + qualquer UUID-group órfão como extra
      const extras = isDivergent ? [deviceGroup] : [];
      const res = await SyncAPI.nuclearWipeMenuItems({ extraGroups: extras });
      if (res.ok) {
        const total = Object.values(res.groupsWiped || {}).reduce(
          (acc, r) => acc + (r.wiped || 0), 0
        );
        toast.success(`💥 Nuclear wipe concluído: ${total} item(ns) apagados em ${Object.keys(res.groupsWiped || {}).length} namespace(s).`);
        setLastResult({ ok: total, fail: 0, errors: [], wipe: true });
      } else {
        toast.error(`Falha: ${res.reason} ${res.error || ''}`);
      }
    } finally {
      setDeleting(false);
      setConfirmNuclear(false);
      setNukeText('');
      await refresh();
    }
  };

  /**
   * v7.53 — Alinha o deviceGroup local para `kaven-default`. Após troca,
   * recarrega a página para os listeners do Firebase reconectarem no
   * novo namespace.
   */
  const handleAlignToCanonical = async () => {
    setMigrating(true);
    try {
      const res = await SyncAPI.migrateDeviceGroupTo(CANONICAL_GROUP);
      if (res.ok) {
        if (res.alreadyAligned) {
          toast.info('Já está alinhado ao kaven-default');
        } else {
          toast.success(`✓ Migrado: ${res.previousGroup} → ${res.newGroup}. Recarregando...`);
          setTimeout(() => window.location.reload(), 1500);
        }
      } else {
        toast.error(`Falha ao migrar: ${res.reason} ${res.error || ''}`);
      }
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="ghost-menu-cleaner-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            data-testid="back-to-admin-btn"
            onClick={() => navigate('/admin')}
            className="text-sm text-slate-500 hover:text-slate-800 mb-2 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao Painel
          </button>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Bug className="w-8 h-8 text-rose-600" />
            Limpeza do Cardápio Fantasma
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Pratos com R$ 0,00 ou sem foto que aparecem no <Link to="/delivery" className="text-blue-600 underline">/delivery</Link>{' '}
            mas não estão no <Link to="/cardapio" className="text-blue-600 underline">/cardapio</Link> do admin
            são resquícios de seeds antigos no Firebase. Aqui você apaga
            <strong> direto do Firebase RTDB</strong>, com tombstone permanente
            (nenhum device futuro pode ressuscitar).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="sync-now-btn"
            onClick={handleSyncNow}
            disabled={syncing || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm font-bold"
            title="Envia TODOS os pratos do Dexie local pro Firebase agora"
          >
            <Upload className={`w-4 h-4 ${syncing ? 'animate-pulse' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar agora'}
          </button>
          <button
            data-testid="refresh-btn"
            onClick={refresh}
            disabled={loading || syncing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium text-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Recarregar
          </button>
        </div>
      </div>

      {/* v7.53 — Banner de divergência de deviceGroup */}
      {isDivergent && (
        <div className="p-4 rounded-lg bg-orange-50 border-2 border-orange-300 text-orange-900 flex items-start gap-3" data-testid="divergence-banner">
          <AlertTriangle className="w-6 h-6 mt-0.5 flex-shrink-0 text-orange-600" />
          <div className="flex-1">
            <div className="font-bold text-base">⚠️ Este aparelho está em um namespace ISOLADO</div>
            <p className="text-sm mt-1">
              DeviceGroup atual: <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">{deviceGroup}</code> —
              diferente do canônico <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">kaven-default</code>.
              <br />
              Por isso este aparelho não vê os mesmos pratos que os outros. Clique abaixo para alinhar.
            </p>
            <button
              data-testid="align-canonical-btn"
              onClick={handleAlignToCanonical}
              disabled={migrating}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white font-bold hover:bg-orange-700 disabled:opacity-50 text-sm"
            >
              <Zap className="w-4 h-4" />
              {migrating ? 'Migrando...' : 'Alinhar para kaven-default'}
            </button>
          </div>
        </div>
      )}

      {/* Status / DeviceGroup */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <StatCard label="Total no Firebase" value={remoteItems.length} testId="stat-remote-total" />
        <StatCard label="Fantasmas detectados" value={phantoms.length} tone={phantoms.length > 0 ? 'rose' : 'emerald'} testId="stat-phantom-count" />
        <StatCard label="No Dexie local" value={localItems.length} testId="stat-local-total" />
        <StatCard label="DeviceGroup" value={deviceGroup ? deviceGroup.slice(0, 18) + (deviceGroup.length > 18 ? '…' : '') : '—'} mono testId="stat-device-group" />
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-3" data-testid="error-banner">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Erro ao ler Firebase</div>
            <div>{error}</div>
            <div className="text-xs text-amber-700 mt-1">
              Verifique se o Firebase está habilitado em <Link to="/config" className="underline">/config</Link> e se está conectado (badge &quot;Cloud Connected&quot;).
            </div>
          </div>
        </div>
      )}

      {lastResult && (
        <div className={`p-4 rounded-lg border text-sm flex items-start gap-3 ${
          lastResult.fail === 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'
        }`} data-testid="result-banner">
          {lastResult.fail === 0 ? <CheckCircle2 className="w-5 h-5 mt-0.5" /> : <XCircle className="w-5 h-5 mt-0.5" />}
          <div className="flex-1">
            <div className="font-semibold">
              {lastResult.wipe ? 'Limpeza total concluída' : 'Exclusão concluída'}: {lastResult.ok} sucesso(s), {lastResult.fail} falha(s).
            </div>
            {lastResult.errors?.length > 0 && (
              <ul className="mt-1 text-xs space-y-0.5 max-h-24 overflow-auto">
                {lastResult.errors.slice(0, 10).map((e, i) => <li key={`${i}-${String(e).slice(0, 40)}`}>• {e}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            data-testid="filter-phantom-only-btn"
            onClick={() => setShowOnlyPhantom(true)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border ${showOnlyPhantom ? 'bg-rose-100 border-rose-300 text-rose-800' : 'bg-white border-slate-300 text-slate-600'}`}
          >
            Só fantasmas ({phantoms.length})
          </button>
          <button
            data-testid="filter-all-btn"
            onClick={() => setShowOnlyPhantom(false)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border ${!showOnlyPhantom ? 'bg-slate-200 border-slate-400 text-slate-900' : 'bg-white border-slate-300 text-slate-600'}`}
          >
            Tudo no Firebase ({remoteItems.length})
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="select-all-phantom-btn"
            onClick={selectAllPhantom}
            disabled={phantoms.length === 0}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40"
          >
            Selecionar todos os fantasmas
          </button>
          <button
            data-testid="clear-selection-btn"
            onClick={clearSelection}
            disabled={selected.size === 0}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            Limpar
          </button>
          <button
            data-testid="delete-selected-btn"
            onClick={() => setConfirmBulk(true)}
            disabled={selected.size === 0 || deleting}
            className="px-3 py-1.5 rounded-md text-sm font-bold bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Excluir {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
        </div>
      </div>

      {/* Items table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="items-table">
          <thead className="bg-slate-50 text-slate-700 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left w-10"></th>
              <th className="px-3 py-2 text-left">Nome</th>
              <th className="px-3 py-2 text-left">Categoria</th>
              <th className="px-3 py-2 text-right w-24">Preço</th>
              <th className="px-3 py-2 text-center w-16">Foto</th>
              <th className="px-3 py-2 text-center w-24">Status</th>
              <th className="px-3 py-2 text-left w-32">Firebase Key</th>
              <th className="px-3 py-2 text-center w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-8 text-slate-500">Carregando do Firebase...</td></tr>
            )}
            {!loading && filteredItems.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400" data-testid="empty-state">
                {showOnlyPhantom
                  ? <><CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" /> Nenhum cardápio fantasma detectado.</>
                  : 'Firebase está vazio para esta deviceGroup.'}
              </td></tr>
            )}
            {!loading && filteredItems.map((item) => {
              const phantom = isLikelyPhantom(item);
              const known = KNOWN_PHANTOM_NAMES.has(item.name);
              const isSelected = selected.has(item.fbKey);
              return (
                <tr
                  key={item.fbKey}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${phantom ? 'bg-rose-50/30' : ''} ${isSelected ? 'bg-blue-50' : ''}`}
                  data-testid={`item-row-${item.fbKey}`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(item.fbKey)}
                      data-testid={`select-${item.fbKey}`}
                      className="w-4 h-4 accent-rose-600"
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    {item.name || <em className="text-slate-400">(sem nome)</em>}
                    {known && <Badge className="ml-2 bg-rose-100 text-rose-700 border-0 text-[10px]">SEED</Badge>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{item.category || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-700">
                    {item.price != null ? `R$ ${Number(item.price).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {item.imageUrl && item.imageUrl.length > 5
                      ? <ImageIcon className="w-4 h-4 text-emerald-600 inline" />
                      : <ImageOff className="w-4 h-4 text-rose-500 inline" />}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {phantom
                      ? <Badge className="bg-rose-600 text-white border-0">FANTASMA</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-800 border-0">OK</Badge>}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500" title={item.stableId || ''}>{item.fbKey}</td>
                  <td className="px-3 py-2">
                    <button
                      data-testid={`delete-single-${item.fbKey}`}
                      onClick={async () => {
                        setDeleting(true);
                        const res = await SyncAPI.deleteRemoteMenuItem({
                          fbKey: item.fbKey,
                          stableId: item.stableId || null,
                          name: item.name || null,
                        });
                        setDeleting(false);
                        if (res.ok) {
                          toast.success(`"${item.name || item.fbKey}" excluído com tombstone permanente.`);
                          refresh();
                        } else {
                          toast.error(`Falha: ${res.error || res.reason}`);
                        }
                      }}
                      disabled={deleting}
                      className="p-1.5 rounded hover:bg-rose-100 text-rose-600 disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Danger Zone — Wipe all menuItems */}
      <div className="border-2 border-rose-300 rounded-lg p-5 bg-rose-50/50 space-y-3">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-rose-700 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-rose-900">Zona de Perigo — Wipe Total do Cardápio</div>
            <p className="text-sm text-rose-800 mt-1">
              Apaga <strong>TODA</strong> a árvore <code className="bg-white px-1 rounded">/{deviceGroup || 'kaven-default'}/menuItems</code> no
              Firebase RTDB e cria tombstones permanentes para cada item. Use somente se nenhum prato listado deve continuar.
              Após o wipe, recadastre os pratos manualmente em <Link to="/cardapio" className="underline">/cardapio</Link>.
            </p>
          </div>
        </div>
        <button
          data-testid="open-wipe-dialog-btn"
          onClick={() => setConfirmWipe(true)}
          disabled={remoteItems.length === 0 || deleting}
          className="px-4 py-2 rounded-md bg-rose-700 text-white font-bold hover:bg-rose-800 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Apagar TODO o cardápio do Firebase
        </button>

        {/* v7.53 — Nuclear Wipe: limpa MÚLTIPLOS namespaces simultaneamente */}
        <div className="border-t border-rose-300 pt-3 mt-3">
          <div className="font-bold text-rose-900 mb-1">💥 Nuclear: Limpar TUDO em todos os namespaces</div>
          <p className="text-sm text-rose-800 mb-2">
            Apaga <code className="bg-white px-1 rounded">menuItems</code> EM{' '}
            <strong>{isDivergent ? '2 namespaces' : '1 namespace'}</strong>:{' '}
            <code className="bg-white px-1 rounded">{deviceGroup}</code>
            {isDivergent && <> + <code className="bg-white px-1 rounded">kaven-default</code></>},
            Dexie local, e <strong>todos os tombstones</strong> (recomeço completo do zero).
          </p>
          <button
            data-testid="open-nuclear-dialog-btn"
            onClick={() => setConfirmNuclear(true)}
            disabled={deleting}
            className="px-4 py-2 rounded-md bg-rose-900 text-white font-bold hover:bg-black disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Bug className="w-4 h-4" />
            💥 Limpar TUDO (local + Firebase)
          </button>
        </div>
      </div>

      {/* Dialog: Confirma exclusão em massa */}
      <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <DialogContent data-testid="confirm-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Excluir {selected.size} item(ns)</DialogTitle>
            <DialogDescription>
              Esta ação <strong>apaga do Firebase RTDB</strong> e cria tombstone permanente.
              Itens excluídos NÃO podem voltar via sync de outros devices.
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-40 overflow-y-auto text-sm space-y-1 bg-slate-50 p-3 rounded">
            {remoteItems.filter((it) => selected.has(it.fbKey)).slice(0, 30).map((it) => (
              <li key={it.fbKey} className="flex items-center gap-2">
                <ChevronRight className="w-3 h-3 text-slate-400" />
                <span className="font-medium">{it.name || '(sem nome)'}</span>
                <span className="text-slate-400">— {it.category}</span>
              </li>
            ))}
            {selected.size > 30 && <li className="text-xs text-slate-500">+{selected.size - 30} mais</li>}
          </ul>
          <DialogFooter>
            <button
              data-testid="cancel-bulk-btn"
              onClick={() => setConfirmBulk(false)}
              className="px-4 py-2 rounded-md bg-white border border-slate-300 text-slate-700"
            >Cancelar</button>
            <button
              data-testid="confirm-bulk-btn"
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="px-4 py-2 rounded-md bg-rose-700 text-white font-bold hover:bg-rose-800 disabled:opacity-40"
            >{deleting ? 'Apagando...' : `Confirmar exclusão de ${selected.size}`}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Wipe total */}
      <Dialog open={confirmWipe} onOpenChange={(o) => { setConfirmWipe(o); if (!o) setWipeText(''); }}>
        <DialogContent data-testid="confirm-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Apagar TODO o cardápio</DialogTitle>
            <DialogDescription>
              Esta ação <strong className="text-rose-700">não tem volta</strong>. Vai apagar os {remoteItems.length} item(ns)
              de <code className="bg-slate-100 px-1 rounded text-xs">/{deviceGroup}/menuItems</code> e tombstoná-los todos.
              Para confirmar, digite <strong className="font-mono bg-slate-100 px-1.5 rounded">APAGAR CARDAPIO</strong>:
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="wipe-confirm-input"
            value={wipeText}
            onChange={(e) => setWipeText(e.target.value)}
            placeholder="APAGAR CARDAPIO"
            className="font-mono"
            autoFocus
          />
          <DialogFooter>
            <button
              data-testid="cancel-wipe-btn"
              onClick={() => { setConfirmWipe(false); setWipeText(''); }}
              className="px-4 py-2 rounded-md bg-white border border-slate-300 text-slate-700"
            >Cancelar</button>
            <button
              data-testid="confirm-wipe-btn"
              onClick={handleWipeAll}
              disabled={wipeText !== 'APAGAR CARDAPIO' || deleting}
              className="px-4 py-2 rounded-md bg-rose-800 text-white font-bold hover:bg-rose-900 disabled:opacity-40"
            >{deleting ? 'Apagando tudo...' : 'CONFIRMAR WIPE TOTAL'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v7.53 — Dialog: Nuclear Wipe (limpa múltiplos namespaces) */}
      <Dialog open={confirmNuclear} onOpenChange={(o) => { setConfirmNuclear(o); if (!o) setNukeText(''); }}>
        <DialogContent data-testid="confirm-nuclear-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-900 flex items-center gap-2">
              💥 Nuclear Wipe — recomeço total
            </DialogTitle>
            <DialogDescription>
              Esta ação apaga <strong className="text-rose-700">EM TODOS OS NAMESPACES</strong>:
              <ul className="mt-2 ml-4 list-disc text-xs space-y-0.5">
                <li><code className="bg-slate-100 px-1 rounded">{deviceGroup}</code>/menuItems</li>
                {isDivergent && <li><code className="bg-slate-100 px-1 rounded">kaven-default</code>/menuItems</li>}
                <li>Tombstones de TODOS os namespaces (recomeço limpo)</li>
                <li>Dexie local (este aparelho)</li>
              </ul>
              <div className="mt-3">
                Para confirmar, digite <strong className="font-mono bg-slate-100 px-1.5 rounded">LIMPAR TUDO</strong>:
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="nuke-confirm-input"
            value={nukeText}
            onChange={(e) => setNukeText(e.target.value)}
            placeholder="LIMPAR TUDO"
            className="font-mono"
            autoFocus
          />
          <DialogFooter>
            <button
              data-testid="cancel-nuke-btn"
              onClick={() => { setConfirmNuclear(false); setNukeText(''); }}
              className="px-4 py-2 rounded-md bg-white border border-slate-300 text-slate-700"
            >Cancelar</button>
            <button
              data-testid="confirm-nuke-btn"
              onClick={handleNuclearWipe}
              disabled={nukeText !== 'LIMPAR TUDO' || deleting}
              className="px-4 py-2 rounded-md bg-rose-900 text-white font-bold hover:bg-black disabled:opacity-40"
            >{deleting ? 'Detonando...' : '💥 EXECUTAR NUCLEAR WIPE'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone, mono, testId }) {
  const toneClasses = {
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  };
  return (
    <div className={`rounded-lg border p-4 ${toneClasses[tone] || 'bg-white border-slate-200 text-slate-900'}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${mono ? 'font-mono text-base' : ''}`}>{value}</div>
    </div>
  );
}
